import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { processSteps } from "@/mocks/homeData";
import JsonLD from "@/components/base/JsonLD";
import { howToSchema } from "@/components/base/PageSchemas";
import useScrollReveal from "@/hooks/useScrollReveal";
import Breadcrumb from "@/components/base/Breadcrumb";

function StepCard({ step, index }: { step: typeof processSteps[0]; index: number }) {
  const reveal = useScrollReveal({ threshold: 0.15 });

  return (
    <div
      ref={reveal.ref}
      className={`relative bg-white rounded-xl p-5 shadow-sm border border-background-200 overflow-hidden transition-all duration-700 ease-out ${
        reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {/* Subtelny gradient w tle */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-primary-500/5 rounded-bl-full" />

      <div className="flex items-start gap-4">
        {/* Numer w kółeczku */}
        <div className="relative z-10 w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-white font-heading font-bold text-sm shadow-md">
          {step.step}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1.5">
            {step.title}
          </h3>
          <p className="text-sm text-foreground-600 leading-[1.7]">
            {step.description}
          </p>
        </div>
      </div>

      {/* Łącznik z następnym krokiem (poza ostatnim) */}
      {index < processSteps.length - 1 && (
        <div className="hidden md:block absolute left-[33px] bottom-0 translate-y-full w-[2px] h-4 bg-primary-200" />
      )}
    </div>
  );
}

export default function JakDzialamPage() {
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const ctaReveal = useScrollReveal({ threshold: 0.2 });

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLD data={howToSchema} />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-24 md:pt-32 pb-8 md:pb-14 bg-background-50">
          <div className="mx-auto max-w-[1440px] px-4 text-center md:px-8 lg:px-16 xl:px-20">
            <div
              ref={headerReveal.ref}
              className={`transition-all duration-700 ease-out ${
                headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <Breadcrumb />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-3 block">
                Jak działam
              </span>
              <h1 className="mx-auto max-w-3xl font-heading text-3xl text-foreground-950 md:text-4xl lg:text-5xl">
                Transparentny proces współpracy
              </h1>
            </div>
          </div>
        </section>

        {/* Intro + kroki */}
        <section className="pb-16 md:pb-24">
          <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
            <div className="mx-auto max-w-5xl">
              <p className="mx-auto mb-8 max-w-3xl text-center text-base leading-relaxed text-foreground-600 md:mb-12">
                Cały proces od pierwszego kontaktu aż do podpisania umowy jest transparentny i przejrzysty. Na każdym etapie pozostaję w kontakcie i odpowiadam na wszystkie pytania.
              </p>

              {/* MOBILE: Karty */}
              <div className="md:hidden flex flex-col gap-4">
                {processSteps.map((step, index) => (
                  <StepCard key={step.step} step={step} index={index} />
                ))}
              </div>

              {/* DESKTOP: Timeline */}
              <div className="hidden md:flex flex-col gap-6">
                {processSteps.map((step, index) => (
                  <StepCard key={step.step} step={step} index={index} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-16 md:pb-24">
          <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
            <div
              ref={ctaReveal.ref}
              className={`text-center transition-all duration-700 ease-out ${
                ctaReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <div className="max-w-md mx-auto bg-primary-500 rounded-2xl p-8 text-white">
                <h3 className="font-heading text-xl font-bold mb-3">
                  Gotowy na pierwszy krok?
                </h3>
                <p className="text-sm text-white/80 mb-6">
                  Umów bezpłatną konsultację i dowiedz się, jak mogę Ci pomóc.
                </p>
                <a
                  href="tel:698084044"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-primary-600 font-semibold rounded-full hover:bg-background-50 hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  Zadzwoń – 698 084 044
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
